import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Calendar, Clock, MapPin, Euro, CheckCircle2, XCircle, Users, ChevronLeft, ChevronRight } from "lucide-react";
import { useAnalytics } from "../hooks/useAnalytics";

const BookingCalendar = () => {
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedPostcode, setSelectedPostcode] = useState("");
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarDates, setCalendarDates] = useState([]);
  const { trackCalendarBooking, trackWhatsAppClick } = useAnalytics();

  const priceData = [
    { sqm: "30", price: 490, label: "30m² Wohnung Pauschalpreis" },
    { sqm: "40", price: 679, label: "40m² Wohnung" },
    { sqm: "50", price: 890, label: "50m² Wohnung" },
    { sqm: "60", price: 990, label: "60m² Wohnung" },
    { sqm: "70", price: 1190, label: "70m² Wohnung" },
    { sqm: "80", price: 1290, label: "80m² Wohnung" },
    { sqm: "90", price: 1390, label: "90m² Wohnung" },
    { sqm: "100", price: 1490, label: "100m² Wohnung" }
  ];

  const monthNames = [
    "Januar", "Februar", "März", "April", "Mai", "Juni",
    "Juli", "August", "September", "Oktober", "November", "Dezember"
  ];

  const dayNames = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

  // Generate calendar for current month
  useEffect(() => {
    const generateCalendar = () => {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const startDate = new Date(firstDay);
      
      // Start from previous month's days to fill the first week
      startDate.setDate(startDate.getDate() - firstDay.getDay());
      
      const calendar = [];
      const today = new Date();
      
      for (let week = 0; week < 6; week++) {
        const weekDays = [];
        for (let day = 0; day < 7; day++) {
          const currentDate = new Date(startDate);
          currentDate.setDate(startDate.getDate() + (week * 7) + day);
          
          const isCurrentMonth = currentDate.getMonth() === month;
          const isToday = currentDate.toDateString() === today.toDateString();
          const isPast = currentDate < today;
          const isSunday = currentDate.getDay() === 0;
          
          // All dates are available except past dates and Sundays
          let available = false;
          if (isCurrentMonth && !isPast && !isSunday) {
            available = true; // All valid dates are now available
          }
          
          weekDays.push({
            date: currentDate,
            day: currentDate.getDate(),
            isCurrentMonth,
            isToday,
            isPast,
            isSunday,
            available,
            dateString: currentDate.toISOString().split('T')[0]
          });
        }
        calendar.push(weekDays);
        
        // Stop if we've covered all days of the month
        if (weekDays[6].date.getMonth() !== month) break;
      }
      
      return calendar;
    };

    setCalendarDates(generateCalendar());
  }, [currentMonth]);

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  // Validate PLZ format (5 digits)
  const isValidPostcode = (plz) => /^\d{5}$/.test(plz);

  const calculateTotal = (netPrice) => {
    const vat = netPrice * 0.19;
    const total = netPrice + vat;
    return { net: netPrice, vat: vat, total: total };
  };

  const handleDateSelect = (dateObj) => {
    if (dateObj.available && dateObj.isCurrentMonth) {
      setSelectedDate(dateObj.dateString);
    }
  };

  const handleBooking = () => {
    if (!selectedDate || !selectedSize || !isValidPostcode(selectedPostcode)) return;

    const selectedSizeObj = priceData.find(p => p.sqm === selectedSize);
    
    // Track calendar booking (GA4)
    trackCalendarBooking(selectedDate, selectedSize);
    // Track WhatsApp click with Google Ads conversion
    trackWhatsAppClick('calendar_booking');

    const selectedDateObj = new Date(selectedDate);
    const dayName = selectedDateObj.toLocaleDateString('de-DE', { weekday: 'long' });
    const dayMonth = selectedDateObj.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: 'long' 
    });
    
    const calculation = calculateTotal(selectedSizeObj.price);
    
    const message = `Hallo, ich möchte einen Termin für Malerarbeiten buchen.

📅 Gewünschter Termin: ${dayName}, ${dayMonth}
🏠 Wohnungsgröße: ${selectedSizeObj.label}
💰 Festpreis: ${calculation.total.toFixed(2)}€ (inkl. MwSt.)
📍 Postleitzahl: ${selectedPostcode}

⚠️ WICHTIG: Preise gelten nur für leere Wohnungen ohne Möbel (apartamenti vuoti senza mobili).

Bitte bestätigen Sie mir diesen Termin. Vielen Dank!`;

    const whatsappUrl = `https://wa.me/4915758183212?text=${encodeURIComponent(message)}`;
    // Use window.open for reliability - this always works even if tracking fails
    window.open(whatsappUrl, '_blank');
  };

  const isBookingComplete = selectedDate && selectedSize && isValidPostcode(selectedPostcode);

  return (
    <section className="py-16 bg-gradient-to-br from-black via-gray-900 to-amber-900">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-amber-400 to-yellow-300 rounded-full mb-6">
            <Calendar className="text-black" size={32} />
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
            Termin buchen
          </h2>
          <p className="text-lg sm:text-xl text-amber-200 max-w-2xl mx-auto">
            Wählen Sie Ihren Wunschtermin und lassen Sie sich ein unverbindliches Angebot erstellen
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Step 1: Calendar Date Selection */}
            <div className="bg-gray-800 rounded-2xl p-4 sm:p-6 border border-amber-500/20">
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-amber-400 text-black rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
                  1
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white">Termin wählen</h3>
              </div>

              {/* Calendar Header */}
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => navigateMonth(-1)}
                  className="p-2 text-amber-300 hover:text-amber-400 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <ChevronLeft size={20} />
                </button>
                <h4 className="text-lg font-bold text-white">
                  {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </h4>
                <button
                  onClick={() => navigateMonth(1)}
                  className="p-2 text-amber-300 hover:text-amber-400 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <ChevronRight size={20} />
                </button>
              </div>

              {/* Calendar Grid */}
              <div className="bg-gray-700 rounded-xl p-4">
                {/* Day Headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {dayNames.map(day => (
                    <div key={day} className="text-center text-xs font-semibold text-amber-300 p-2">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Days */}
                <div className="space-y-1">
                  {calendarDates.map((week, weekIndex) => (
                    <div key={weekIndex} className="grid grid-cols-7 gap-1">
                      {week.map((dateObj, dayIndex) => (
                        <button
                          key={dayIndex}
                          onClick={() => handleDateSelect(dateObj)}
                          disabled={!dateObj.available || !dateObj.isCurrentMonth}
                          className={`
                            p-2 text-sm rounded-lg transition-all duration-200 relative
                            ${!dateObj.isCurrentMonth 
                              ? 'text-gray-500 cursor-not-allowed' 
                              : dateObj.available
                              ? selectedDate === dateObj.dateString
                                ? 'bg-gradient-to-r from-amber-400 to-yellow-300 text-black font-bold'
                                : 'text-white hover:bg-gray-600 border border-gray-600'
                              : 'text-gray-500 cursor-not-allowed bg-gray-800'
                            }
                            ${dateObj.isToday ? 'ring-2 ring-amber-400' : ''}
                          `}
                        >
                          {dateObj.day}
                          {dateObj.isCurrentMonth && dateObj.available && (
                            <div className="absolute top-0 right-0 w-2 h-2">
                              <div className="w-full h-full bg-green-400 rounded-full"></div>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {selectedDate && (
                <div className="mt-4 p-3 bg-amber-900/20 rounded-lg border border-amber-500/30">
                  <div className="flex items-center gap-2 text-amber-200">
                    <Clock size={16} />
                    <span className="text-sm">Terminzeit: 08:00 - 16:00 Uhr</span>
                  </div>
                </div>
              )}
            </div>

            {/* Step 2: Size Selection */}
            <div className="bg-gray-800 rounded-2xl p-6 border border-amber-500/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-amber-400 text-black rounded-full flex items-center justify-center font-bold">
                  2
                </div>
                <h3 className="text-xl font-bold text-white">Wohnungsgröße</h3>
              </div>

              <div className="space-y-3 max-h-80 overflow-y-auto">
                {priceData.map((item) => (
                  <button
                    key={item.sqm}
                    onClick={() => setSelectedSize(item.sqm)}
                    className={`w-full p-4 rounded-xl transition-all duration-200 text-left ${
                      selectedSize === item.sqm
                        ? 'bg-gradient-to-r from-amber-400 to-yellow-300 text-black border-2 border-amber-300'
                        : 'bg-gray-700 text-white hover:bg-gray-600 border border-gray-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-bold">{item.label}</div>
                        <div className="text-sm opacity-80">Netto zzgl. MwSt.</div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-lg">{item.price}€</div>
                        <div className="text-sm opacity-80">
                          {calculateTotal(item.price).total.toFixed(0)}€ inkl.
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Step 3: Postcode Input - Simple availability check */}
            <div className="bg-gray-800 rounded-2xl p-6 border border-amber-500/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-amber-400 text-black rounded-full flex items-center justify-center font-bold">
                  3
                </div>
                <h3 className="text-xl font-bold text-white">Sind wir auch in Ihrer Region?</h3>
              </div>

              {/* PLZ Input */}
              <div className="space-y-4">
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    placeholder="Ihre Postleitzahl eingeben..."
                    value={selectedPostcode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                      setSelectedPostcode(value);
                    }}
                    maxLength={5}
                    className="w-full pl-10 pr-4 py-4 bg-gray-700 text-white placeholder-gray-400 rounded-xl border border-gray-600 focus:border-amber-400 focus:outline-none text-lg"
                  />
                </div>
                
                <p className="text-gray-400 text-sm">
                  Einsatzgebiet: Baden-Württemberg. Geben Sie Ihre PLZ ein, um die Verfügbarkeit anzufragen.
                </p>
                
                {selectedPostcode && !isValidPostcode(selectedPostcode) && (
                  <p className="text-amber-400 text-sm">
                    Bitte geben Sie eine vollständige 5-stellige Postleitzahl ein.
                  </p>
                )}
                
                {isValidPostcode(selectedPostcode) && (
                  <div className="bg-amber-400/10 border border-amber-400/30 rounded-lg p-3">
                    <p className="text-amber-300 text-sm">
                      ✓ PLZ {selectedPostcode} wird in Ihre Anfrage übernommen. Wir prüfen die Verfügbarkeit und melden uns.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Booking Summary & CTA */}
          {isBookingComplete && (
            <div className="mt-8 bg-gradient-to-r from-amber-400 to-yellow-300 rounded-2xl p-8 text-black">
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-4">Ihre Buchungsübersicht</h3>
                
                <div className="grid md:grid-cols-3 gap-6 mb-6">
                  <div className="bg-black/10 rounded-xl p-4">
                    <Calendar className="mx-auto mb-2" size={24} />
                    <div className="font-bold">
                      {new Date(selectedDate).toLocaleDateString('de-DE', { weekday: 'long' })}
                    </div>
                    <div className="text-sm">
                      {new Date(selectedDate).toLocaleDateString('de-DE', { 
                        day: '2-digit', 
                        month: 'long' 
                      })}
                    </div>
                  </div>
                  
                  <div className="bg-black/10 rounded-xl p-4">
                    <Users className="mx-auto mb-2" size={24} />
                    <div className="font-bold">
                      {priceData.find(p => p.sqm === selectedSize)?.label}
                    </div>
                    <div className="text-sm">Professionelle Malerarbeiten</div>
                  </div>
                  
                  <div className="bg-black/10 rounded-xl p-4">
                    <Euro className="mx-auto mb-2" size={24} />
                    <div className="font-bold text-xl">
                      {calculateTotal(priceData.find(p => p.sqm === selectedSize)?.price).total.toFixed(2)}€
                    </div>
                    <div className="text-sm">inkl. MwSt.</div>
                  </div>
                </div>

                <Button
                  onClick={handleBooking}
                  className="bg-black text-amber-400 hover:bg-gray-800 px-8 py-4 text-lg font-bold rounded-xl transition-all duration-200 hover:scale-105"
                >
                  Jetzt unverbindlich anfragen
                </Button>
                
                <p className="text-sm mt-4 opacity-80">
                  PLZ: {selectedPostcode} • Kostenlose Beratung • Festpreisgarantie
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Features */}
        <div className="mt-16 grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="text-center">
            <div className="w-12 h-12 bg-gradient-to-r from-amber-400 to-yellow-300 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="text-black" size={24} />
            </div>
            <h4 className="text-white font-bold mb-2">Festpreisgarantie</h4>
            <p className="text-amber-200 text-sm">
              Transparente Preise ohne versteckte Kosten
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-gradient-to-r from-amber-400 to-yellow-300 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="text-black" size={24} />
            </div>
            <h4 className="text-white font-bold mb-2">Fachbetrieb</h4>
            <p className="text-amber-200 text-sm">
              Qualifizierte Fachkräfte mit langjähriger Erfahrung
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-gradient-to-r from-amber-400 to-yellow-300 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="text-black" size={24} />
            </div>
            <h4 className="text-white font-bold mb-2">Pünktlich & Zuverlässig</h4>
            <p className="text-amber-200 text-sm">
              Zuverlässige und professionelle Ausführung
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingCalendar;